import { useState, useEffect } from 'react';
import axios from 'axios';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams } from 'react-router-dom';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';
import { ADMIN_PERMISSIONS } from '../../constants/adminPermissions';

const ACTIONS = [
    { key: 'view', label: 'View' },
    { key: 'create', label: 'Create' },
    { key: 'edit', label: 'Edit' },
    { key: 'delete', label: 'Delete' },
];

const RoleForm = () => {
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(isEditMode);
    const [saving, setSaving] = useState(false);

    const [name, setName] = useState("");
    // { productKey: { view: bool, create: bool, edit: bool, delete: bool } }
    const [permissions, setPermissions] = useState({});

    useEffect(() => {
        if (!isEditMode) {
            setLoading(false);
            return;
        }
        const fetchRole = async () => {
            try {
                const { data } = await axios.get(`/api/v1/admin/role/${id}`);
                setName(data.role.name);
                setPermissions(data.role.permissions || {});
            } catch (error) {
                enqueueSnackbar(error?.response?.data?.message || "Failed to load role", { variant: "error" });
                navigate('/admin/roles');
            } finally {
                setLoading(false);
            }
        };
        fetchRole();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const isChecked = (moduleKey, actionKey) => Boolean(permissions[moduleKey]?.[actionKey]);

    const toggleAction = (moduleKey, actionKey) => {
        setPermissions((prev) => {
            const current = prev[moduleKey] || { view: false, create: false, edit: false, delete: false };
            const next = { ...current, [actionKey]: !current[actionKey] };

            // Create/Edit/Delete without View doesn't make practical sense
            // (the page to do those things on wouldn't be reachable) — so
            // checking any of them auto-checks View too.
            if (actionKey !== 'view' && next[actionKey]) {
                next.view = true;
            }
            // Unchecking View with nothing else meaningful left just clears
            // the whole module back to all-false, keeping state tidy.
            if (actionKey === 'view' && !next.view) {
                next.create = false;
                next.edit = false;
                next.delete = false;
            }

            return { ...prev, [moduleKey]: next };
        });
    };

    const toggleAllForModule = (moduleKey, checked) => {
        setPermissions((prev) => ({
            ...prev,
            [moduleKey]: { view: checked, create: checked, edit: checked, delete: checked },
        }));
    };

    const submitHandler = async (e) => {
        e.preventDefault();

        if (!name.trim()) return enqueueSnackbar("Please enter a role name", { variant: "warning" });

        const payload = { name, permissions };

        setSaving(true);
        try {
            if (isEditMode) {
                await axios.put(`/api/v1/admin/role/${id}`, payload);
                enqueueSnackbar("Role updated", { variant: "success" });
            } else {
                await axios.post('/api/v1/admin/role/new', payload);
                enqueueSnackbar("Role created", { variant: "success" });
            }
            navigate('/admin/roles');
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to save role", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <BackdropLoader />;

    return (
        <>
            <MetaData title={`Admin: ${isEditMode ? "Edit" : "New"} Role | 1313 Mart`} />
            {saving && <BackdropLoader />}

            <form onSubmit={submitHandler} className="flex flex-col gap-4 bg-white rounded-lg shadow p-4 sm:p-6" id="roleform">

                <h1 className="text-lg font-medium uppercase">{isEditMode ? "Edit" : "New"} Role</h1>

                <TextField
                    label="Role Name"
                    variant="outlined"
                    size="small"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    sx={{ maxWidth: 320 }}
                    helperText="e.g. 'Content Editor', 'Order Manager'"
                />

                <div>
                    <p className="font-medium text-gray-700 mb-2">Permissions</p>
                    <div className="border rounded-lg overflow-x-auto">
                        <table className="w-full text-sm min-w-[520px]">
                            <thead>
                                <tr className="border-b bg-gray-50 text-left">
                                    <th className="py-2 px-4">Page</th>
                                    {ACTIONS.map((a) => (
                                        <th key={a.key} className="py-2 px-3 text-center">{a.label}</th>
                                    ))}
                                    <th className="py-2 px-3 text-center">All</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ADMIN_PERMISSIONS.map((p) => {
                                    const allChecked = ACTIONS.every((a) => isChecked(p.key, a.key));
                                    return (
                                        <tr key={p.key} className="border-b last:border-0">
                                            <td className="py-2 px-4 font-medium text-gray-700">{p.label}</td>
                                            {ACTIONS.map((a) => (
                                                <td key={a.key} className="py-1 px-3 text-center">
                                                    <Checkbox
                                                        size="small"
                                                        checked={isChecked(p.key, a.key)}
                                                        onChange={() => toggleAction(p.key, a.key)}
                                                    />
                                                </td>
                                            ))}
                                            <td className="py-1 px-3 text-center">
                                                <Checkbox
                                                    size="small"
                                                    checked={allChecked}
                                                    onChange={(e) => toggleAllForModule(p.key, e.target.checked)}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                        Checking Create, Edit, or Delete automatically checks View too — a page can't be
                        edited if it can't be seen. "Users" and "Role Management" are always admin-only and
                        don't appear here.
                    </p>
                </div>

                <div className="flex justify-end">
                    <input form="roleform" type="submit" className="bg-primary-orange uppercase px-6 py-3 text-white font-medium rounded shadow hover:shadow-lg cursor-pointer" value={isEditMode ? "Save Changes" : "Create Role"} />
                </div>

            </form>
        </>
    );
};

export default RoleForm;